/**
 * web/src/hooks/useEvent.tsx
 * Active-event state, shared via React context (F16). Previously each
 * `useEvent()` call held its own `useState`, so the sidebar EventSelector and
 * the page Outlet were independent cells — switching events in the sidebar
 * never updated the open page (every event-scoped query kept reading the
 * stale id until a remount). Now a single <EventProvider> (mounted in
 * AppShell) owns the state + localStorage persistence and every consumer
 * reads the same cell, so a switch re-renders all mounted pages in place.
 *
 * Persisted in localStorage under `pixel:active-event-id`. Seeds from the
 * first event on a fresh login, and self-heals when the stored id is absent
 * from the loaded list (archived/deleted active event).
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useEvents } from './queries/useEvents';
import type { Event } from '../api/types';

const KEY = 'pixel:active-event-id';

/**
 * Reads the persisted active eventId. Returns null on private-mode storage
 * failures — the provider then defaults to the first event.
 */
function readKey(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** The shared active-event context value. */
export interface EventContextValue {
  eventId: string | null;
  event: Event | null;
  events: Event[] | undefined;
  setEventId: (id: string) => void;
  isPending: boolean;
}

const EventCtx = createContext<EventContextValue | null>(null);

/**
 * Owns the active-event state for the whole app. Mount EXACTLY ONCE, above
 * every consumer (AppShell, wrapping both the sidebar and the page Outlet).
 *
 * @sideEffects writes localStorage on setEventId and on the default/self-heal seed
 */
export function EventProvider({ children }: { children: ReactNode }) {
  const { data: events, isPending } = useEvents();
  const [eventId, setEventIdState] = useState<string | null>(readKey);

  // Seed on fresh login AND self-heal a stale id: pick the first event when
  // unset or when the stored id is no longer in the loaded list.
  useEffect(() => {
    if (!events || events.length === 0) return;
    if (!eventId || !events.some((e) => e.id === eventId)) {
      // Prefer an active event for the seed — the sidebar selector only
      // offers active events, so defaulting to an archived one would strand
      // the user on a selection they can't navigate back to.
      const fallback = (events.find((e) => e.isActive) ?? events[0]).id;
      try {
        localStorage.setItem(KEY, fallback);
      } catch {
        // localStorage may be disabled in private mode — keep state-only.
      }
      setEventIdState(fallback);
    }
  }, [eventId, events]);

  const setEventId = useCallback((id: string) => {
    try {
      localStorage.setItem(KEY, id);
    } catch {
      // ignore
    }
    setEventIdState(id);
  }, []);

  const event = events?.find((e) => e.id === eventId) ?? null;
  const value = useMemo<EventContextValue>(
    () => ({ eventId, event, events, setEventId, isPending }),
    [eventId, event, events, setEventId, isPending],
  );

  return <EventCtx.Provider value={value}>{children}</EventCtx.Provider>;
}

/**
 * Returns the shared `{ eventId, event, events, setEventId, isPending }`.
 * Must be called within an <EventProvider>.
 *
 * @returns active event tuple plus setter
 * @sideEffects none (the provider owns persistence)
 */
export function useEvent(): EventContextValue {
  const ctx = useContext(EventCtx);
  if (!ctx) {
    throw new Error('useEvent must be used within an <EventProvider>');
  }
  return ctx;
}
